import { useState, useCallback, useMemo } from 'react';
import type React from 'react';
import { CheckCircle, PackageCheck, AlertTriangle, Loader2, User, Phone, Mail, MapPin, ChevronDown, Hash, Calendar, Minus, Plus, Download, Warehouse, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Button, Card } from '@/components/ui';
import { pickupApi } from '@/services/api/pickupApi';
import { ordersApi } from '@/services/api/ordersApi';
import { queryKeys } from '@/services/api/queryClient';
import toast from 'react-hot-toast';
import type { QRScanResponse, HandoffSource } from '@/services/api/pickupApi';
import type { OrderItemDetail } from '@/types/models';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const downloadHandoffPdf = async (orderId: number, orderNumber: string) => {
  try {
    const blob = await pickupApi.downloadHandoffPdf(orderId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `handoff_${orderNumber}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Акт выдачи скачан');
  } catch (err) {
    toast.error(extractErrorMessage(err, 'Ошибка при скачивании акта'));
  }
};

interface HandoffConfirmationProps {
  scanData: QRScanResponse;
  onComplete: () => void;
  onCancel: () => void;
}

const HandoffConfirmation: React.FC<HandoffConfirmationProps> = ({
  scanData,
  onComplete,
  onCancel,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [givenQuantities, setGivenQuantities] = useState<Record<number, number>>({});
  const [showDetails, setShowDetails] = useState(false);
  const [handoffNotes, setHandoffNotes] = useState('');
  const [selectedSource, setSelectedSource] = useState<HandoffSource>('warehouse');

  const { data: orderDetail, isLoading: isLoadingItems } = useQuery({
    queryKey: queryKeys.order(scanData.order_id),
    queryFn: () => ordersApi.getById(scanData.order_id),
  });

  const items = orderDetail?.items ?? [];
  const isReHandoff = orderDetail?.status === 'PARTIALLY_HANDED_OFF';
  const maxForItem = useCallback(
    (item: OrderItemDetail) => isReHandoff ? item.quantity_received - item.quantity_handed_off : item.quantity_received,
    [isReHandoff],
  );
  const activeItems = useMemo(
    () => isReHandoff ? items.filter((i) => maxForItem(i) > 0) : items,
    [isReHandoff, items, maxForItem],
  );
  const verifiedCount = activeItems.filter((i) => (givenQuantities[i.id] ?? 0) === maxForItem(i)).length;
  const allVerified = activeItems.length > 0 && verifiedCount === activeItems.length;
  const hasAnyItems = activeItems.some((i) => (givenQuantities[i.id] ?? 0) > 0);
  const isPartialHandoff = activeItems.some((i) => (givenQuantities[i.id] ?? 0) < maxForItem(i));
  const needsNotes = isReHandoff || isPartialHandoff;
  const canConfirm = hasAnyItems && (!needsNotes || handoffNotes.trim().length > 0);

  const setItemQty = useCallback((id: number, qty: number) => {
    setGivenQuantities((prev) => ({ ...prev, [id]: qty }));
  }, []);

  const fillAll = useCallback(() => {
    if (allVerified) {
      setGivenQuantities({});
    } else {
      const filled: Record<number, number> = {};
      for (const item of activeItems) {
        filled[item.id] = maxForItem(item);
      }
      setGivenQuantities(filled);
    }
  }, [allVerified, activeItems, maxForItem]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      const handoffItems = items.map((item) => ({
        order_item_id: item.id,
        quantity_handed_off: givenQuantities[item.id] ?? 0,
      }));
      await pickupApi.confirmHandoff(scanData.order_id, {
        items: handoffItems,
        handoff_notes: handoffNotes || null,
        source: selectedSource,
      });
      setIsSuccess(true);
      toast.success(isReHandoff ? 'Товары довыданы!' : isPartialHandoff ? 'Заказ частично выдан!' : 'Заказ выдан!');

      // Auto-redirect back to scanner after 3 seconds
      setTimeout(() => {
        onComplete();
      }, 3000);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            'Ошибка при подтверждении выдачи';
      toast.error(message);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isSuccess ? (
        <motion.div
          key="success"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <Card className="overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400" />
            <Card.Body className="py-10 flex flex-col items-center text-center">
              {/* Success pulse animation */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 200, damping: 15 }}
                className="relative mb-6"
              >
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                </div>
                {/* Ripple rings */}
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-emerald-300"
                  initial={{ scale: 1, opacity: 0.6 }}
                  animate={{ scale: 1.8, opacity: 0 }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }}
                />
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-emerald-200"
                  initial={{ scale: 1, opacity: 0.4 }}
                  animate={{ scale: 2.2, opacity: 0 }}
                  transition={{ duration: 1.2, delay: 0.3, repeat: Infinity, ease: 'easeOut' }}
                />
              </motion.div>

              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-bold text-gray-900 mb-1"
              >
                Заказ выдан!
              </motion.h3>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-sm text-gray-500"
              >
                №{scanData.order_number} — выдача подтверждена
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-4"
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadHandoffPdf(scanData.order_id, scanData.order_number)}
                >
                  <Download className="w-4 h-4" />
                  Скачать акт выдачи
                </Button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-xs text-gray-400 mt-4"
              >
                Возврат к сканеру через 3 сек...
              </motion.p>
            </Card.Body>
          </Card>
        </motion.div>
      ) : (
        <motion.div
          key="confirm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400" />
            <Card.Body className="py-6 flex flex-col items-center text-center gap-4">
              <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
                <PackageCheck className="w-7 h-7 text-amber-500" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {isReHandoff ? 'Довыдать заказ?' : 'Подтвердить выдачу?'}
                </h3>
                <p className="text-sm text-gray-500">
                  Заказ №{scanData.order_number}
                  {scanData.customer_name && ` — ${scanData.customer_name}`}
                </p>
              </div>

              {/* Customer & order details — expandable */}
              {(scanData.customer_name || orderDetail) && (
                <div className="w-full">
                  <button
                    type="button"
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors mx-auto"
                  >
                    <User className="w-3.5 h-3.5" />
                    Детали заказа и клиента
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-3 p-3 bg-blue-50/60 rounded-xl text-left space-y-2">
                          {/* Customer info */}
                          {scanData.customer_name && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <User className="w-3.5 h-3.5 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-blue-400 uppercase tracking-wider leading-none mb-0.5">Клиент</p>
                                <p className="text-sm font-medium text-gray-900 truncate">{scanData.customer_name}</p>
                              </div>
                            </div>
                          )}

                          {scanData.customer_phone && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <Phone className="w-3.5 h-3.5 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-blue-400 uppercase tracking-wider leading-none mb-0.5">Телефон</p>
                                <a href={`tel:${scanData.customer_phone}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors">
                                  {scanData.customer_phone}
                                </a>
                              </div>
                            </div>
                          )}

                          {scanData.customer_email && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <Mail className="w-3.5 h-3.5 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-blue-400 uppercase tracking-wider leading-none mb-0.5">Email</p>
                                <p className="text-sm font-medium text-gray-900 truncate">{scanData.customer_email}</p>
                              </div>
                            </div>
                          )}

                          {/* Order meta */}
                          {scanData.shelf_location && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-3.5 h-3.5 text-purple-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-purple-400 uppercase tracking-wider leading-none mb-0.5">Полка</p>
                                <p className="text-sm font-medium text-gray-900">{scanData.shelf_location}</p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Hash className="w-3.5 h-3.5 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">ID заказа</p>
                              <p className="text-sm font-medium text-gray-900">{scanData.order_id}</p>
                            </div>
                          </div>

                          {orderDetail?.created_at && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Calendar className="w-3.5 h-3.5 text-gray-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">Дата заказа</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {new Date(orderDetail.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </p>
                              </div>
                            </div>
                          )}

                          {orderDetail?.invoice_number && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Hash className="w-3.5 h-3.5 text-gray-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">Накладная</p>
                                <p className="text-sm font-medium text-gray-900">{orderDetail.invoice_number}</p>
                              </div>
                            </div>
                          )}

                          {orderDetail?.notes && (
                            <div className="mt-1 p-2 bg-white/60 rounded-lg">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Заметки</p>
                              <p className="text-xs text-gray-700">{orderDetail.notes}</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl text-left w-full">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Убедитесь, что клиент получил все товары из заказа.
                  Действие нельзя отменить.
                </p>
              </div>

              <div className="w-full text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Проверено: {verifiedCount} из {activeItems.length}
                  </span>
                  {activeItems.length > 0 && (
                    <button
                      type="button"
                      onClick={fillAll}
                      className="text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
                    >
                      {allVerified ? 'Сбросить' : 'Заполнить все'}
                    </button>
                  )}
                </div>

                {isLoadingItems ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
                    <span className="ml-2 text-xs text-gray-400">Загрузка товаров…</span>
                  </div>
                ) : items.length === 0 ? (
                  <div className="py-4 text-center text-xs text-gray-400">
                    Нет товаров в заказе
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-50">
                    {items.map((item, idx) => {
                      const max = maxForItem(item);
                      const alreadyDone = isReHandoff && max === 0;
                      const given = givenQuantities[item.id] ?? 0;
                      const isFull = !alreadyDone && given === max;
                      const isPartial = !alreadyDone && given > 0 && given < max;

                      if (alreadyDone) {
                        return (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03, duration: 0.2 }}
                            className="flex items-center gap-2 px-3 py-2.5 bg-gray-50/70 opacity-60"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm leading-tight truncate text-gray-400 line-through">
                                {item.name}
                              </p>
                              <p className="text-[11px] text-gray-300 mt-0.5">{item.sku}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                              <span className="text-[11px] text-emerald-500 font-medium">выдано</span>
                            </div>
                          </motion.div>
                        );
                      }

                      return (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.03, duration: 0.2 }}
                          className={`flex items-center gap-2 px-3 py-2.5 transition-colors ${
                            isFull
                              ? 'bg-emerald-50/70'
                              : isPartial
                                ? 'bg-amber-50/50'
                                : 'bg-white'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm leading-tight truncate ${
                              isFull ? 'text-gray-500' : 'text-gray-800'
                            }`}>
                              {item.name}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              {item.sku}
                              {isReHandoff && item.quantity_handed_off > 0 && (
                                <span className="ml-1 text-amber-500">(уже выдано: {item.quantity_handed_off})</span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => setItemQty(item.id, Math.max(0, given - 1))}
                              disabled={given <= 0}
                              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className={`w-10 text-center text-sm font-mono tabular-nums font-medium ${
                              isFull ? 'text-emerald-600' : isPartial ? 'text-amber-600' : 'text-gray-400'
                            }`}>
                              {given}
                            </span>
                            <button
                              type="button"
                              onClick={() => setItemQty(item.id, Math.min(max, given + 1))}
                              disabled={given >= max}
                              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                            <span className="text-[11px] text-gray-400 ml-0.5 tabular-nums">
                              / {max}
                            </span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              {isReHandoff && (
                <div className="w-full">
                  <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Источник товара
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedSource('warehouse')}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                        selectedSource === 'warehouse'
                          ? 'border-amber-300 bg-amber-50 text-amber-700 shadow-sm'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Warehouse className="w-4 h-4" />
                      Со склада
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedSource('external')}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                        selectedSource === 'external'
                          ? 'border-amber-300 bg-amber-50 text-amber-700 shadow-sm'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Из другого источника
                    </button>
                  </div>
                  {selectedSource === 'external' && (
                    <p className="text-xs text-gray-400 mt-1.5 ml-1">
                      Товар не будет списан со склада
                    </p>
                  )}
                </div>
              )}

              {((isPartialHandoff && hasAnyItems) || isReHandoff) && (
                <div className="w-full">
                  <label className="block text-xs font-semibold text-amber-600 mb-1">
                    {isReHandoff ? 'Комментарий к довыдаче (обязательно)' : 'Комментарий (обязательно при частичной выдаче)'}
                  </label>
                  <textarea
                    value={handoffNotes}
                    onChange={(e) => setHandoffNotes(e.target.value)}
                    placeholder={isReHandoff ? 'Укажите причину довыдачи…' : 'Укажите причину частичной выдаче…'}
                    className="w-full px-3 py-2 text-sm border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                    rows={2}
                  />
                </div>
              )}

              <div className="flex gap-3 w-full">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={onCancel}
                  disabled={isConfirming}
                >
                  Отмена
                </Button>
                <Button
                  variant="primary"
                  className="flex-1 !bg-emerald-600 hover:!bg-emerald-700"
                  onClick={handleConfirm}
                  loading={isConfirming}
                  disabled={isConfirming || !canConfirm || isLoadingItems}
                >
                  <PackageCheck className="w-4 h-4" />
                  {isReHandoff ? 'Довыдать' : isPartialHandoff && hasAnyItems ? 'Частичная выдача' : 'Подтвердить выдачу'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HandoffConfirmation;
